// Drilling Costs Service - connects to Postgres via Azure Function
import { authService } from '../auth/authService';

class DrillingCostsService {
  constructor() {
    // Use dedicated wolfwalker-api Function App (Python Flex Consumption with VNet)
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    
    // Azure Function endpoints for drilling costs
    this.activityOutliersUrl = `${this.functionAppUrl}/api/activity_outliers`;
    this.standbyDetailUrl = `${this.functionAppUrl}/api/standby_detail`;
    this.invoiceSummaryUrl = `${this.functionAppUrl}/api/invoice_summary`;
    this.costLedgerUrl = `${this.functionAppUrl}/api/cost_ledger`;
    this.coringActivityUrl = `${this.functionAppUrl}/api/coring_activity`;
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken();
      const userInfo = authService.getUserInfo();
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown'
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  /**
   * Get activity outliers data
   * Returns drilling activity outliers with z-score analysis
   */
  async getActivityOutliers(options = {}) {
    const startTime = Date.now();
    console.log('🔍 [Activity Outliers] Starting fetch...', options);
    
    try {
      const {
        limit = 5000,
        activity_code,
        activity_description,
        hole_name,
        outlier_flag
      } = options;

      const headers = await this.getAuthenticatedHeaders();
      let url = `${this.activityOutliersUrl}?limit=${limit}`;
      
      if (activity_code) url += `&activity_code=${encodeURIComponent(activity_code)}`;
      if (activity_description) url += `&activity_description=${encodeURIComponent(activity_description)}`;
      if (hole_name) url += `&hole_name=${encodeURIComponent(hole_name)}`;
      if (outlier_flag) url += `&outlier_flag=${encodeURIComponent(outlier_flag)}`;

      console.log('📡 [Activity Outliers] Request URL:', url);
      console.log('🔑 [Activity Outliers] Headers:', { ...headers, Authorization: 'Bearer ***' });

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      console.log('📨 [Activity Outliers] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Activity Outliers] Error response:', errorText);
        throw new Error(`Failed to fetch activity outliers: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      const duration = Date.now() - startTime;
      
      console.log('✅ [Activity Outliers] Success:', {
        count: result.count || result.data?.length || 0,
        duration: `${duration}ms`,
        hasData: !!result.data,
        dataLength: result.data?.length || 0
      });

      return {
        success: result.success,
        data: result.data || [],
        count: result.count
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error('❌ [Activity Outliers] Error after', duration, 'ms:', error);
      console.error('❌ [Activity Outliers] Stack:', error.stack);
      throw error;
    }
  }

  /**
   * Get standby detail data
   * Returns drilling standby time details
   */
  async getStandbyDetail(options = {}) {
    const startTime = Date.now();
    console.log('🔍 [Standby Detail] Starting fetch...', options);
    
    try {
      const {
        limit = 5000,
        rig_name,
        hole_name,
        activity_code,
        activity_description
      } = options;

      const headers = await this.getAuthenticatedHeaders();
      let url = `${this.standbyDetailUrl}?limit=${limit}`;
      
      if (rig_name) url += `&rig_name=${encodeURIComponent(rig_name)}`;
      if (hole_name) url += `&hole_name=${encodeURIComponent(hole_name)}`;
      if (activity_code) url += `&activity_code=${encodeURIComponent(activity_code)}`;
      if (activity_description) url += `&activity_description=${encodeURIComponent(activity_description)}`;

      console.log('📡 [Standby Detail] Request URL:', url);
      console.log('🔑 [Standby Detail] Headers:', { ...headers, Authorization: 'Bearer ***' });

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      console.log('📨 [Standby Detail] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Standby Detail] Error response:', errorText);
        throw new Error(`Failed to fetch standby detail: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      const duration = Date.now() - startTime;
      
      console.log('✅ [Standby Detail] Success:', {
        count: result.count || result.data?.length || 0,
        duration: `${duration}ms`,
        hasData: !!result.data,
        dataLength: result.data?.length || 0
      });

      return {
        success: result.success,
        data: result.data || [],
        count: result.count
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error('❌ [Standby Detail] Error after', duration, 'ms:', error);
      console.error('❌ [Standby Detail] Stack:', error.stack);
      throw error;
    }
  }

  /**
   * Get invoice summary data
   * Returns invoice reconciliation data by period
   */
  async getInvoiceSummary(options = {}) {
    const startTime = Date.now();
    console.log('🔍 [Invoice Summary] Starting fetch...', options);
    
    try {
      const {
        limit = 5000,
        cost_category,
        period_id,
        period_start_date,
        period_end_date
      } = options;

      const headers = await this.getAuthenticatedHeaders();
      let url = `${this.invoiceSummaryUrl}?limit=${limit}`;
      
      if (cost_category) url += `&cost_category=${encodeURIComponent(cost_category)}`;
      if (period_id) url += `&period_id=${encodeURIComponent(period_id)}`;
      if (period_start_date) url += `&period_start_date=${encodeURIComponent(period_start_date)}`;
      if (period_end_date) url += `&period_end_date=${encodeURIComponent(period_end_date)}`;

      console.log('📡 [Invoice Summary] Request URL:', url);
      console.log('🔑 [Invoice Summary] Headers:', { ...headers, Authorization: 'Bearer ***' });

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      console.log('📨 [Invoice Summary] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Invoice Summary] Error response:', errorText);
        throw new Error(`Failed to fetch invoice summary: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      const duration = Date.now() - startTime;
      
      console.log('✅ [Invoice Summary] Success:', {
        count: result.count || result.data?.length || 0,
        duration: `${duration}ms`,
        hasData: !!result.data,
        dataLength: result.data?.length || 0
      });

      return {
        success: result.success,
        data: result.data || [],
        count: result.count
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error('❌ [Invoice Summary] Error after', duration, 'ms:', error);
      console.error('❌ [Invoice Summary] Stack:', error.stack);
      throw error;
    }
  }

  /**
   * Get cost ledger data
   * Returns unified cost ledger for timeline analysis
   */
  async getCostLedger(options = {}) {
    const startTime = Date.now();
    console.log('🔍 [Cost Ledger] Starting fetch...', options);
    
    try {
      const {
        limit = 5000,
        cost_type,
        cost_category,
        period_id,
        activity_date,
        start_date,
        end_date,
        rig_name,
        hole_name,
        project
      } = options;

      const headers = await this.getAuthenticatedHeaders();
      let url = `${this.costLedgerUrl}?limit=${limit}`;
      
      if (cost_type) url += `&cost_type=${encodeURIComponent(cost_type)}`;
      if (cost_category) url += `&cost_category=${encodeURIComponent(cost_category)}`;
      if (period_id) url += `&period_id=${encodeURIComponent(period_id)}`;
      if (activity_date) url += `&activity_date=${encodeURIComponent(activity_date)}`;
      if (start_date) url += `&start_date=${encodeURIComponent(start_date)}`;
      if (end_date) url += `&end_date=${encodeURIComponent(end_date)}`;
      if (rig_name) url += `&rig_name=${encodeURIComponent(rig_name)}`;
      if (hole_name) url += `&hole_name=${encodeURIComponent(hole_name)}`;
      if (project) url += `&project=${encodeURIComponent(project)}`;

      console.log('📡 [Cost Ledger] Request URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      console.log('📨 [Cost Ledger] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Cost Ledger] Error response:', errorText);
        throw new Error(`Failed to fetch cost ledger: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      const duration = Date.now() - startTime;
      
      console.log('✅ [Cost Ledger] Success:', {
        count: result.count || result.data?.length || 0,
        duration: `${duration}ms`
      });

      return {
        success: result.success,
        data: result.data || [],
        count: result.count
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error('❌ [Cost Ledger] Error after', duration, 'ms:', error);
      throw error;
    }
  }

  /**
   * Get coring activity data
   * Returns drilling coring activity with penetration metrics
   * 
   * ⚠️ UPDATED: API now returns billable as 'Y'/'N' string (not boolean)
   * ⚠️ UPDATED: Depth fields renamed: distance_drilled_from/to (not start_depth/end_depth)
   * ⚠️ UPDATED: Activity fields: activity_type/activity (not activity_code/activity_description)
   */
  async getCoringActivity(options = {}) {
    const startTime = Date.now();
    console.log('🔍 [Coring Activity] Starting fetch...', options);
    
    try {
      const {
        limit = 5000,
        hole_name,
        drill,
        shift,
        report_date,
        start_date,
        end_date,
        billable = true,
        activity,
        activity_type
      } = options;

      const headers = await this.getAuthenticatedHeaders();
      let url = `${this.coringActivityUrl}?limit=${limit}`;
      
      // billable parameter accepts boolean or 'Y'/'N' string
      if (billable !== undefined) url += `&billable=${billable}`;
      if (hole_name) url += `&hole_name=${encodeURIComponent(hole_name)}`;
      if (drill) url += `&drill=${encodeURIComponent(drill)}`;
      if (shift) url += `&shift=${encodeURIComponent(shift)}`;
      if (report_date) url += `&report_date=${encodeURIComponent(report_date)}`;
      if (start_date) url += `&start_date=${encodeURIComponent(start_date)}`;
      if (end_date) url += `&end_date=${encodeURIComponent(end_date)}`;
      if (activity) url += `&activity=${encodeURIComponent(activity)}`;
      if (activity_type) url += `&activity_type=${encodeURIComponent(activity_type)}`;

      console.log('📡 [Coring Activity] Request URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      console.log('📨 [Coring Activity] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Coring Activity] Error response:', errorText);
        throw new Error(`Failed to fetch coring activity: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      const duration = Date.now() - startTime;
      
      console.log('✅ [Coring Activity] Success:', {
        count: result.data?.length || 0,
        duration: `${duration}ms`
      });

      return {
        success: result.success,
        data: result.data || []
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error('❌ [Coring Activity] Error after', duration, 'ms:', error);
      throw error;
    }
  }

  /**
   * Get all dashboard data for display
   */
  async getDashboardData() {
    const startTime = Date.now();
    console.log('🚀 [Dashboard] Starting to fetch all data...');
    
    try {
      console.log('⏳ [Dashboard] Fetching 3 endpoints in parallel...');
      
      const [outliers, standby, invoices] = await Promise.all([
        this.getActivityOutliers({ limit: 5000 }).catch(err => {
          console.error('⚠️ [Dashboard] Activity Outliers failed:', err.message);
          return { success: false, data: [], count: 0, error: err.message };
        }),
        this.getStandbyDetail({ limit: 5000 }).catch(err => {
          console.error('⚠️ [Dashboard] Standby Detail failed:', err.message);
          return { success: false, data: [], count: 0, error: err.message };
        }),
        this.getInvoiceSummary({ limit: 5000 }).catch(err => {
          console.error('⚠️ [Dashboard] Invoice Summary failed:', err.message);
          return { success: false, data: [], count: 0, error: err.message };
        })
      ]);

      const duration = Date.now() - startTime;
      
      console.log('✅ [Dashboard] All requests completed:', {
        duration: `${duration}ms`,
        outliers: { count: outliers.data?.length || 0, success: outliers.success, error: outliers.error },
        standby: { count: standby.data?.length || 0, success: standby.success, error: standby.error },
        invoices: { count: invoices.data?.length || 0, success: invoices.success, error: invoices.error }
      });

      // Check if any requests failed
      const failures = [];
      if (!outliers.success) failures.push('Activity Outliers');
      if (!standby.success) failures.push('Standby Detail');
      if (!invoices.success) failures.push('Invoice Summary');
      
      if (failures.length > 0) {
        console.warn('⚠️ [Dashboard] Some endpoints failed:', failures.join(', '));
      }

      return {
        outliers: outliers.data || [],
        standby: standby.data || [],
        invoices: invoices.data || [],
        success: true,
        partialFailures: failures.length > 0 ? failures : null
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error('❌ [Dashboard] Complete failure after', duration, 'ms:', error);
      console.error('❌ [Dashboard] Stack:', error.stack);
      throw error;
    }
  }

  /**
   * Filter data by search term
   */
  filterData(data, searchTerm) {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(item => 
      JSON.stringify(item).toLowerCase().includes(term)
    );
  }

  /**
   * Filter data by date range
   */
  filterByDateRange(data, dateFilter, customStartDate = null, customEndDate = null) {
    if (!dateFilter || dateFilter === 'all') return data;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return data.filter(item => {
      const reportDate = new Date(item.report_date);

      // Handle custom date range
      if (dateFilter === 'custom') {
        if (!customStartDate || !customEndDate) return true;
        const startDate = new Date(customStartDate);
        const endDate = new Date(customEndDate);
        endDate.setHours(23, 59, 59, 999);
        return reportDate >= startDate && reportDate <= endDate;
      }

      // Handle specific period formats like "2025-12-P1" or "2025-12-P2"
      if (dateFilter.match(/^\d{4}-\d{2}-P[12]$/)) {
        const [yearMonth, period] = dateFilter.split('-P');
        const [year, month] = yearMonth.split('-').map(Number);
        
        if (period === '1') {
          // Period 1: 1st to 15th
          const startDate = new Date(year, month - 1, 1);
          const endDate = new Date(year, month - 1, 15, 23, 59, 59);
          return reportDate >= startDate && reportDate <= endDate;
        } else {
          // Period 2: 16th to end of month
          const startDate = new Date(year, month - 1, 16);
          const endDate = new Date(year, month, 0, 23, 59, 59); // Last day of month
          return reportDate >= startDate && reportDate <= endDate;
        }
      }

      switch (dateFilter) {
        case 'week': {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return reportDate >= weekAgo;
        }
        case 'month': {
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          return reportDate >= startOfMonth;
        }
        case 'year': {
          const startOfYear = new Date(now.getFullYear(), 0, 1);
          return reportDate >= startOfYear;
        }
        default:
          return true;
      }
    });
  }

  /**
   * Calculate summary statistics for outliers
   */
  calculateOutlierStats(data) {
    if (!data || data.length === 0) {
      return {
        totalRecords: 0,
        extremeOutliers: 0,
        totalExcessCost: 0,
        avgZScore: 0,
        topActivity: null
      };
    }

    const extremeOutliers = data.filter(item => 
      item.outlier_flag && item.outlier_flag.includes('Extreme')
    ).length;

    const totalExcessCost = data.reduce((sum, item) => 
      sum + (parseFloat(item.excess_cost_if_billable) || 0), 0
    );

    const avgZScore = data.reduce((sum, item) => 
      sum + (parseFloat(item.z_score) || 0), 0
    ) / data.length;

    // Find most common activity
    const activityCounts = {};
    data.forEach(item => {
      const activity = item.activity_description || 'Unknown';
      activityCounts[activity] = (activityCounts[activity] || 0) + 1;
    });
    const topActivity = Object.keys(activityCounts).reduce((a, b) => 
      activityCounts[a] > activityCounts[b] ? a : b, null
    );

    return {
      totalRecords: data.length,
      extremeOutliers,
      totalExcessCost: totalExcessCost.toFixed(2),
      avgZScore: avgZScore.toFixed(2),
      topActivity
    };
  }

  /**
   * Calculate summary statistics for standby
   */
  calculateStandbyStats(data) {
    if (!data || data.length === 0) {
      return {
        totalRecords: 0,
        totalStandbyHours: 0,
        totalStandbyCost: 0,
        avgStandbyHours: 0,
        topRig: null
      };
    }

    const totalStandbyHours = data.reduce((sum, item) => 
      sum + (parseFloat(item.standby_hours) || 0), 0
    );

    const totalStandbyCost = data.reduce((sum, item) => 
      sum + (parseFloat(item.standby_cost) || 0), 0
    );

    const avgStandbyHours = totalStandbyHours / data.length;

    // Find rig with most standby
    const rigCounts = {};
    data.forEach(item => {
      const rig = item.rig_name || 'Unknown';
      rigCounts[rig] = (rigCounts[rig] || 0) + 1;
    });
    const topRig = Object.keys(rigCounts).reduce((a, b) => 
      rigCounts[a] > rigCounts[b] ? a : b, null
    );

    return {
      totalRecords: data.length,
      totalStandbyHours: totalStandbyHours.toFixed(1),
      totalStandbyCost: totalStandbyCost.toFixed(2),
      avgStandbyHours: avgStandbyHours.toFixed(1),
      topRig
    };
  }

  /**
   * Calculate summary statistics for invoice data
   */
  calculateInvoiceStats(data) {
    if (!data || data.length === 0) {
      return {
        totalCategories: 0,
        totalLineItems: 0,
        grandTotal: 0,
        avgCategoryTotal: 0,
        categories: []
      };
    }

    const totalLineItems = data.reduce((sum, item) => 
      sum + (parseInt(item.line_items) || 0), 0
    );

    const grandTotal = data.reduce((sum, item) => 
      sum + (parseFloat(item.category_total) || 0), 0
    );

    const avgCategoryTotal = grandTotal / data.length;

    // Get unique categories with their totals
    const categoryMap = {};
    data.forEach(item => {
      const cat = item.cost_category || 'Unknown';
      if (!categoryMap[cat]) {
        categoryMap[cat] = 0;
      }
      categoryMap[cat] += parseFloat(item.category_total) || 0;
    });

    const categories = Object.entries(categoryMap).map(([name, total]) => ({
      name,
      total: total.toFixed(2),
      percentage: ((total / grandTotal) * 100).toFixed(1)
    })).sort((a, b) => parseFloat(b.total) - parseFloat(a.total));

    return {
      totalCategories: Object.keys(categoryMap).length,
      totalLineItems,
      grandTotal: grandTotal.toFixed(2),
      avgCategoryTotal: avgCategoryTotal.toFixed(2),
      categories
    };
  }

  /**
   * Group invoice data by period
   */
  groupByPeriod(data) {
    const periodMap = {};
    
    data.forEach(item => {
      const period = item.period_id || 'Unknown';
      if (!periodMap[period]) {
        periodMap[period] = {
          period_id: period,
          period_start_date: item.period_start_date,
          period_end_date: item.period_end_date,
          categories: [],
          period_total: parseFloat(item.period_total) || 0
        };
      }
      
      periodMap[period].categories.push({
        cost_category: item.cost_category,
        category_total: parseFloat(item.category_total) || 0,
        line_items: parseInt(item.line_items) || 0,
        total_quantity: parseFloat(item.total_quantity) || 0,
        pct_of_period_total: parseFloat(item.pct_of_period_total) || 0
      });
    });

    // Sort categories within each period by category_total descending
    Object.values(periodMap).forEach(period => {
      period.categories.sort((a, b) => b.category_total - a.category_total);
    });

    return Object.values(periodMap).sort((a, b) => 
      b.period_id.localeCompare(a.period_id)
    );
  }

  /**
   * Get unique values for filters
   */
  getUniqueValues(data, field) {
    const values = new Set();
    data.forEach(item => {
      if (item[field]) values.add(item[field]);
    });
    return Array.from(values).sort();
  }

  /**
   * Sort data by field and direction
   */
  sortData(data, field, direction = 'asc') {
    return [...data].sort((a, b) => {
      const aVal = a[field] ?? '';
      const bVal = b[field] ?? '';
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }

  /**
   * Normalize rig names to handle inconsistencies
   * "Rig 10" -> "Rig 10"
   * "10" -> "Rig 10"
   * "Drill 10" -> "Rig 10"
   */
  normalizeRigName(rigName) {
    if (!rigName) return null;
    
    const str = String(rigName).trim();
    
    // Extract number from various formats
    const match = str.match(/(\d+)/);
    if (match) {
      return `Rig ${match[1]}`;
    }
    
    return str;
  }

  /**
   * Aggregate timeline data - combine cost ledger with coring activity
   * Groups by date and groupBy field (rig_name or shift)
   * 
   * IMPORTANT: Cost ledger uses 'rig_name', coring activity uses 'drill' for rig
   * We normalize both to use the same key for proper joining
   */
  aggregateTimelineData(costLedgerData, coringActivityData, groupBy = 'rig_name') {
    const grouped = {};
    
    console.log('🔄 [Timeline Aggregation] Starting aggregation...');
    console.log('📊 Input data:', {
      costRecords: costLedgerData.length,
      coringRecords: coringActivityData.length,
      groupBy
    });
    
    // First, aggregate costs by date and groupBy field
    costLedgerData.forEach(item => {
      const date = item.activity_date;  // Using activity_date instead of report_date
      // Cost ledger uses 'rig_name' for rig, 'shift' for shift
      let groupKey = groupBy === 'rig_name' ? item.rig_name : item.shift;
      
      // Normalize rig names to handle "Rig 10" vs "10" vs "Drill 10"
      if (groupBy === 'rig_name') {
        groupKey = this.normalizeRigName(groupKey);
      }
      
      if (!date || !groupKey) return;
      
      if (!grouped[date]) {
        grouped[date] = { date };
      }
      
      // Initialize group if needed
      if (!grouped[date][groupKey]) {
        grouped[date][groupKey] = {
          cost: 0,
          meters: 0,
          shifts: 0
        };
      }
      
      // Sum costs
      grouped[date][groupKey].cost += parseFloat(item.line_item_cost) || 0;
    });
    
    console.log('💰 After cost aggregation:', {
      uniqueDates: Object.keys(grouped).length,
      sampleDate: Object.keys(grouped)[0],
      sampleGroups: grouped[Object.keys(grouped)[0]] ? Object.keys(grouped[Object.keys(grouped)[0]]).filter(k => k !== 'date') : []
    });
    
    // Add penetration data from coring activity
    // IMPORTANT: Coring activity uses 'drill' for rig name, not 'rig_name'
    coringActivityData.forEach(item => {
      const date = item.report_date;
      // Coring activity uses 'drill' for rig, 'shift' for shift
      let groupKey = groupBy === 'rig_name' ? item.drill : item.shift;
      
      // Normalize rig names to match cost ledger format
      if (groupBy === 'rig_name') {
        groupKey = this.normalizeRigName(groupKey);
      }
      
      if (!date || !groupKey) return;
      
      if (!grouped[date]) {
        grouped[date] = { date };
      }
      
      if (!grouped[date][groupKey]) {
        grouped[date][groupKey] = {
          cost: 0,
          meters: 0,
          shifts: 0
        };
      }
      
      // Sum penetration
      grouped[date][groupKey].meters += parseFloat(item.penetration) || 0;
      grouped[date][groupKey].shifts += 1;
    });
    
    console.log('📏 After meters aggregation:', {
      uniqueDates: Object.keys(grouped).length,
      sampleDate: Object.keys(grouped)[0],
      sampleData: grouped[Object.keys(grouped)[0]]
    });
    
    // Convert to array format suitable for charting
    const chartData = Object.keys(grouped).map(date => {
      const dayData = { date };
      let totalCost = 0;
      let totalMeters = 0;
      
      // Flatten group data
      Object.keys(grouped[date]).forEach(key => {
        if (key === 'date') return;
        
        const data = grouped[date][key];
        dayData[`${key}_cost`] = data.cost;
        dayData[`${key}_meters`] = data.meters;
        
        totalCost += data.cost;
        totalMeters += data.meters;
      });
      
      dayData.totalCost = totalCost;
      dayData.totalMeters = totalMeters;
      dayData.costPerMeter = totalMeters > 0 ? totalCost / totalMeters : 0;
      
      return dayData;
    });
    
    // Sort by date
    const sortedData = chartData.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    console.log('✅ [Timeline Aggregation] Complete:', {
      totalDays: sortedData.length,
      totalCost: sortedData.reduce((sum, d) => sum + d.totalCost, 0).toFixed(2),
      totalMeters: sortedData.reduce((sum, d) => sum + d.totalMeters, 0).toFixed(1),
      avgCostPerMeter: sortedData.reduce((sum, d) => sum + d.costPerMeter, 0) / sortedData.length,
      daysWithCost: sortedData.filter(d => d.totalCost > 0).length,
      daysWithMeters: sortedData.filter(d => d.totalMeters > 0).length,
      daysWithBoth: sortedData.filter(d => d.totalCost > 0 && d.totalMeters > 0).length
    });
    
    return sortedData;
  }

  /**
   * Calculate timeline statistics
   */
  calculateTimelineStats(timelineData) {
    if (!timelineData || timelineData.length === 0) {
      return {
        totalCost: 0,
        totalMeters: 0,
        avgCostPerMeter: 0,
        totalShifts: 0,
        bestDay: null,
        worstDay: null
      };
    }

    const totalCost = timelineData.reduce((sum, day) => sum + (day.totalCost || 0), 0);
    const totalMeters = timelineData.reduce((sum, day) => sum + (day.totalMeters || 0), 0);
    const avgCostPerMeter = totalMeters > 0 ? totalCost / totalMeters : 0;
    
    // Find best and worst days
    const daysWithData = timelineData.filter(d => d.totalMeters > 0);
    const bestDay = daysWithData.reduce((best, day) => 
      !best || day.costPerMeter < best.costPerMeter ? day : best, null
    );
    const worstDay = daysWithData.reduce((worst, day) => 
      !worst || day.costPerMeter > worst.costPerMeter ? day : worst, null
    );

    return {
      totalCost: totalCost.toFixed(2),
      totalMeters: totalMeters.toFixed(1),
      avgCostPerMeter: avgCostPerMeter.toFixed(2),
      totalShifts: daysWithData.length,
      bestDay: bestDay ? {
        date: bestDay.date,
        costPerMeter: bestDay.costPerMeter.toFixed(2)
      } : null,
      worstDay: worstDay ? {
        date: worstDay.date,
        costPerMeter: worstDay.costPerMeter.toFixed(2)
      } : null
    };
  }
}

// Export singleton instance
export const drillingCostsService = new DrillingCostsService();
export default drillingCostsService;

